import { useState } from "react";
import { Download, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { getUTM, trackEvent } from "@/lib/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CatalogDownload() {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email) return toast.error("Nombre y email son requeridos");
    setLoading(true);
    try {
      const utm = getUTM();
      const res = await fetch(`${API}/leads/catalog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          utm_source: utm.utm_source || null,
          utm_medium: utm.utm_medium || null,
          utm_campaign: utm.utm_campaign || null,
          utm_term: utm.utm_term || null,
          utm_content: utm.utm_content || null,
          referrer: utm.referrer || document.referrer || null,
        }),
      });
      if (!res.ok) throw new Error("error");
      // Evento para GTM/GA4 (mantiene nombre estándar) y como custom evento interno.
      // El backend YA registra 'catalog_download' automáticamente al crear el lead,
      // por eso usamos 'catalog_form_submitted' del lado cliente para NO duplicar.
      trackEvent("catalog_form_submitted", { email: form.email });
      setSuccess(true);
      // open PDF in new tab (instant download)
      window.open("/catalogo_flexshade.pdf", "_blank");
      toast.success("¡Catálogo descargado y enviado a tu email!");
    } catch (err) {
      toast.error("Hubo un problema. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="catalogo" className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-neutral-900 via-[#1a0505] to-black text-white">
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(249,115,22,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(220,38,38,0.3) 0%, transparent 45%)'
      }} />
      <div className="relative container-x grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <span className="eyebrow bg-orange-500/10 border-orange-500/30" style={{color: '#fdba74'}}>Catálogo oficial · Gratis</span>
          <h2 className="mt-4 section-title text-4xl md:text-6xl font-black">
            Descarga el <span className="text-orange-500">catálogo completo</span> de FlexShade
          </h2>
          <p className="mt-4 text-white/75 text-lg max-w-xl">
            Todos los modelos, especificaciones técnicas, colores y galería de proyectos en un solo documento PDF.
            <span className="block mt-2 text-sm text-orange-200">Te lo abrimos al instante y también te llega por correo para que lo tengas siempre a la mano.</span>
          </p>
          <div className="mt-6 flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-2"><FileText className="w-4 h-4"/> PDF · ~2 MB</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400"/> Gratuito</span>
          </div>
        </div>

        <div className="bg-white text-neutral-900 rounded-2xl p-6 md:p-8 shadow-2xl">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-black">¡Catálogo enviado!</h3>
              <p className="text-neutral-600 mb-2 text-sm">Se descargó automáticamente y te lo mandamos por correo a <strong>{form.email}</strong>.</p>
              <p className="text-neutral-500 mb-4 text-xs">Si no se abrió la descarga, haz clic aquí:</p>
              <a href="/catalogo_flexshade.pdf" target="_blank" rel="noreferrer" className="btn-primary px-5 py-3 rounded-lg inline-flex items-center gap-2">
                <Download className="w-5 h-5" /> Descargar PDF de nuevo
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="catalog-form">
              <h3 className="section-title text-xl md:text-2xl font-black">Recibe el catálogo en 10 segundos</h3>
              <p className="text-sm text-neutral-500">Lo abrimos al instante y también te lo mandamos a tu correo.</p>
              <div>
                <label className="form-label">Nombre *</label>
                <input data-testid="catalog-nombre" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Tu nombre" className="form-input" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input data-testid="catalog-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tucorreo@email.com" className="form-input" />
              </div>
              <div>
                <label className="form-label">Teléfono (opcional)</label>
                <input data-testid="catalog-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Ej: 81 1234 5678" className="form-input" inputMode="tel" />
              </div>
              <button type="submit" data-testid="catalog-submit" disabled={loading} className="btn-primary w-full py-3 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Download className="w-5 h-5" />
                {loading ? "Enviando..." : "Descargar catálogo gratis"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
